import { RefObject, useEffect, useLayoutEffect, useRef } from 'react'

import debounce from 'lodash/debounce'

import { useWindowDimensions } from '@/hooks/client/use-window-dimensions'
import { PageType } from '@/types/nav-types'

const interpolateBackgroundColor = (scrollY: number, height: number | null) => {
  if (!height) {
    return 'transparent'
  }
  const progress = Math.min(scrollY / height, 1)
  // Interpolate from rgba(0,0,0,0) to rgb(254, 249, 240)
  const r = Math.round(0 + (254 - 0) * progress)
  const g = Math.round(0 + (249 - 0) * progress)
  const b = Math.round(0 + (240 - 0) * progress)
  const a = progress
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

interface ScrollAdjustProps {
  pageType: PageType
  isDarkNav: boolean
  isMobileOverlayOpen: boolean
  isNavActive: boolean
  navRef: RefObject<HTMLElement | null>
  setMenuVisible: (visible: boolean) => void
  setMenuTextWhite: (white: boolean) => void
}

// This hook writes the nav's background straight to the DOM, which React never
// reverts — so a value set on the previous route survives a soft navigation. On
// a light → dark route change the className flips to `bg-transparent` but the
// inline `#fef9f0` outranks it, and the bar paints cream over the new page's
// navy hero until this runs. Running BEFORE paint removes that frame.
// (useEffect on the server: useLayoutEffect warns during SSR.)
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

const scrollOffset = 20 // Start hiding navbar after scrolling first 20 pixels
// Home nav: keep the menu text white while the interpolated bg is still below
// this opacity (the dark video hero shows through). Above it, the bg is beige
// enough that dark text reads better. Tunable.
const TEXT_WHITE_PROGRESS = 0.5

// When the nav is in the viewport, on the home page, we have to change the background to be transparent.
// We also need to set the text color to white, which we do by telling the caller that the nav is
export const useScrollAdjust = ({
  pageType,
  isDarkNav,
  isMobileOverlayOpen,
  isNavActive,
  navRef,
  setMenuVisible,
  setMenuTextWhite,
}: ScrollAdjustProps) => {
  const { height } = useWindowDimensions(true)
  const lastScrollYRef = useRef(0)
  const rafIdRef = useRef<number | null>(null)
  const lastMenuVisibleRef = useRef(true)
  const lastMenuTextWhiteRef = useRef(pageType === 'home' || isDarkNav)

  useIsoLayoutEffect(() => {
    const updateScrollState = () => {
      const currentScrollY = window.scrollY
      const lastScrollY = lastScrollYRef.current
      const isScrollingDown = currentScrollY > lastScrollY
      const isScrollingUp = currentScrollY < lastScrollY
      const isAtTop = currentScrollY === 0
      const hasScrolledPastOffset = currentScrollY > scrollOffset

      // Determine menu visibility
      // Always visible if at top OR if menu is active
      let menuVisible: boolean
      if (isAtTop || isNavActive) {
        menuVisible = true
      } else {
        // If menu is not active and scrolled past offset
        if (hasScrolledPastOffset) {
          // Only change visibility based on actual scroll direction
          if (isScrollingUp) {
            // Scrolling up -> show menu. Except on compare pages: the sticky
            // buy bar owns the top edge while scrolling back up, and the nav
            // only re-appears once the user reaches the top (the `isAtTop` /
            // `hasScrolledPastOffset` branches). See sticky-bar-shell.tsx —
            // its TOP_EXIT_PX handoff matches `scrollOffset` above.
            menuVisible = pageType === 'compare' ? lastMenuVisibleRef.current : true
          } else if (isScrollingDown) {
            // Scrolling down -> hide menu
            menuVisible = false
          } else {
            // Scroll position hasn't changed -> maintain previous state
            menuVisible = lastMenuVisibleRef.current
          }
        } else {
          // Always show if not scrolled past offset
          menuVisible = true
        }
      }

      // Determine background color + text color together.
      let bgColor: string
      let menuTextWhite: boolean
      if (isDarkNav) {
        // Dark routes open with the hero extending behind the topbar. Preserve
        // that transparency around the top of the page, then use an opaque
        // navy surface when the sticky nav reappears over lighter content.
        bgColor = isMobileOverlayOpen || hasScrolledPastOffset ? '#142546' : 'transparent'
        menuTextWhite = true
      } else if (isNavActive) {
        // A menu is open, dark text. The mobile sheet is white, so the bar
        // matches it; the desktop band is cream, so the bar keeps its cream
        // and the two read as one surface (also over the home hero).
        bgColor = isMobileOverlayOpen ? '#ffffff' : '#fef9f0'
        menuTextWhite = false
      } else if (pageType !== 'home') {
        // Not home page -> base color, dark text
        bgColor = '#fef9f0'
        menuTextWhite = false
      } else {
        // Home page: bg interpolates transparent -> beige over the first
        // viewport. Keep the text WHITE while the bg is still mostly
        // transparent (dark video hero showing through). The old check
        // (`bgColor === 'transparent'`) only matched at exactly scrollY 0, so
        // scrolling up to near the top showed unreadable black text on the
        // near-transparent nav.
        const progress = height ? Math.min(currentScrollY / height, 1) : 0
        bgColor = isAtTop ? 'transparent' : interpolateBackgroundColor(currentScrollY, height)
        menuTextWhite = progress < TEXT_WHITE_PROGRESS
      }

      // Update state only if values changed to avoid unnecessary re-renders
      if (menuVisible !== lastMenuVisibleRef.current) {
        setMenuVisible(menuVisible)
        lastMenuVisibleRef.current = menuVisible
      }

      if (menuTextWhite !== lastMenuTextWhiteRef.current) {
        setMenuTextWhite(menuTextWhite)
        lastMenuTextWhiteRef.current = menuTextWhite
      }

      // Apply background color and shadow directly to DOM to avoid re-renders
      if (navRef.current) {
        navRef.current.style.backgroundColor = bgColor
        // The light bar is flat — no shadow at rest, scrolled, or with a menu
        // open — so it merges with the cream band below it. Dark routes keep
        // a faint shadow once the opaque navy bar sits over lighter content.
        navRef.current.style.boxShadow =
          isDarkNav && hasScrolledPastOffset && !isNavActive ? 'rgba(0,0,0,0.08) 0px 4px 12px 0px' : ''
      }

      lastScrollYRef.current = currentScrollY
      rafIdRef.current = null
    }

    const handleScroll = () => {
      // Cancel any pending RAF to avoid stacking
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
      }

      rafIdRef.current = requestAnimationFrame(updateScrollState)
    }

    // Debounce the scroll handler for performance
    const debouncedHandleScroll = debounce(handleScroll, 10)

    // Initial state update, plus a re-read on the next frame: after a
    // client-side navigation the framework's scroll-to-top can apply AFTER this
    // effect's first synchronous run, so the first read may see the previous
    // page's scroll position and leave the nav stuck on its beige scrolled-state
    // when returning to the transparent home hero. The rAF re-read picks up the
    // settled scroll. (Idempotent, so harmless on a normal load.)
    updateScrollState()
    const initialRaf = requestAnimationFrame(updateScrollState)

    // Set up scroll listener
    window.addEventListener('scroll', debouncedHandleScroll, { passive: true })

    // Cleanup
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll)
      cancelAnimationFrame(initialRaf)
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
      }
    }
  }, [isDarkNav, isMobileOverlayOpen, isNavActive, pageType, height, navRef, setMenuVisible, setMenuTextWhite])
}
